import { createContext, useContext, useEffect, useMemo, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";

import { auth } from "@/api/client";
import { useSession } from "@/auth/session";
import type { CompanyOption } from "@/components/CompanyFilter";
import { useSticky } from "@/lib/hooks/useSticky";

/** WHOSE DATA THE CONSOLE IS SHOWING -- chosen once, in the app bar (D-134).
 *
 *  Dattatray, 2026-10-28: *"Company selection only makes sense for Super
 *  admin, rest all users only one company will be allocated. So I think this
 *  top bar shall have that company selection rather than having it in every
 *  selector box ... every page need not have a company select."*
 *
 *  Before this, Fleet, three FOTA pages, Asset Data and four Registry pages
 *  each carried their own company dropdown and their own copy of the answer,
 *  and nothing kept them agreeing: switching company on Live Window did not
 *  switch it on Commands. Now there is ONE answer and every page reads it.
 *
 *  WHAT `company` MEANS, by caller:
 *    cross-company (super admin)   "" = ALL companies; else one company id.
 *                                  Persisted per login for the tab, so a
 *                                  reload keeps the choice and a different
 *                                  login never inherits it.
 *    scoped (everyone else)        their own company id, always. The server
 *                                  scopes regardless (deps.Scope); carrying
 *                                  the id here means a page that REQUIRES a
 *                                  named company -- Commands, FOTA Update --
 *                                  already has one and asks nothing.
 *
 *  `companies` + `scoped` come from `/auth/v2/companies`, which decides
 *  through the same `Scope` dependency every module route uses, so the bar
 *  can never offer a company a page would then refuse. `scoped` is the
 *  SERVER's word on whether the list was filtered -- CompanyFilter keys off
 *  it, never off the length or the role (see its docstring for the bug).
 *
 *  THE PAGES THAT MUST NOT RUN AT "ALL". Commands stops vehicles and FOTA
 *  Update pushes firmware fleet-wide; both were built to refuse an
 *  "All companies" opening state on Dattatray's instruction. They keep that
 *  rule: with the bar at ALL they list nothing and say to narrow it.
 *  `cross && !company` is that condition, spelled out as `needsOne`.
 */
export interface CompanyScope {
  /** "" = all companies (cross-company caller only); else one company id. */
  company: string;
  setCompany: (id: string) => void;
  companies: CompanyOption[];
  /** The server filtered the list to the caller -- one company, no chooser. */
  scoped: boolean;
  /** The caller may look across companies at all. */
  cross: boolean;
  /** True when a page that needs ONE company has been given ALL. */
  needsOne: boolean;
  /** The name of the one company in scope, for a page that shows it. */
  companyName: string | null;
}

const Ctx = createContext<CompanyScope | null>(null);

export function CompanyScopeProvider({ children }: { children: ReactNode }) {
  const { me } = useSession();
  const cross = !!me?.cross_company;
  // Keyed by login so one person's choice never becomes the next person's
  // default on a shared machine; a scoped caller never reads it.
  const [picked, setPicked] = useSticky("scope", `company.${me?.login_id ?? "-"}`, "");

  const q = useQuery({
    queryKey: ["scope", "companies", me?.login_id],
    queryFn: auth.companies,
    enabled: !!me,
    staleTime: 60_000,
  });

  // A company that has since vanished from the list (deleted, or a different
  // login restored the same key) must not stay selected: the bar would show
  // "All companies" while every request carried a dead id.
  useEffect(() => {
    if (!cross || !picked || !q.data) return;
    if (!q.data.companies.some((c) => c.company_id === picked)) setPicked("");
  }, [cross, picked, q.data, setPicked]);

  const value = useMemo<CompanyScope>(() => {
    const companies = q.data?.companies ?? [];
    const scoped = q.data?.scoped ?? !cross;
    const company = cross ? picked : (me?.company_id ?? "");
    const one = companies.find((c) => c.company_id === company);
    return {
      company,
      setCompany: (id) => { if (cross) setPicked(id); },
      companies,
      scoped,
      cross,
      needsOne: cross && company === "",
      companyName: one?.name ?? (company ? (me?.company_name ?? company) : null),
    };
  }, [cross, picked, q.data, me?.company_id, me?.company_name, setPicked]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useCompanyScope(): CompanyScope {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useCompanyScope outside CompanyScopeProvider");
  return ctx;
}
